import React, { useState, useEffect } from 'react';
import { Phone, Menu, X, User, LogOut, ChevronDown, Shield } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getDatabase, ref, onValue } from 'firebase/database';
import logo from './images/logo2.png';

const Navbar = ({ isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  const auth = getAuth();
  const database = getDatabase();

  // Check if user has admin permissions
  const isAdmin = userProfile?.role === 'admin' || userProfile?.permissions?.admin === true;
  
  // Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      
      // If user is logged in, fetch their profile data
      if (currentUser) {
        const userRef = ref(database, 'users/' + currentUser.uid);
        const userListener = onValue(userRef, (snapshot) => {
          setUserProfile(snapshot.val());
        });
        
        return () => {
          // Clean up listener
          userListener();
        }; 
      } else {
        setUserProfile(null);
      }
    });
    
    return () => unsubscribe();
  }, [auth, database]);
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setUserMenuOpen(false); // Close user menu if open
  };
  
  // Toggle user menu
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Close mobile menu when navigating
  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    navigate(path);
  };
  
  // Combine glass effect with scroll state
  const navbarClass = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
    scrolled 
      ? 'backdrop-blur-md bg-white/80 shadow-lg' 
      : isHomePage 
        ? 'backdrop-blur-sm bg-black/20' 
        : 'bg-white shadow-md'
  }`;
  
  // Text color changes based on scroll and page
  const textColorClass = scrolled ? "text-gray-800" : (isHomePage ? "text-white" : "text-gray-800");
  
  return (
    <div className={navbarClass}>
      {/* Top banner */}
      <div className="bg-black text-white py-2 px-4 text-center">
        <p className="text-xs sm:text-sm flex items-center justify-center gap-2">
          <span className="hidden sm:inline">Transform your vehicle today! Call or text for expert detailing</span>
          <span className="sm:hidden">Call for expert detailing</span>
          <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
          <span className="font-semibold">(630) 708-2674</span>
        </p>
      </div>
      
      {/* Navigation header */}
      <header className={`py-3 px-3 sm:py-4 ${scrolled ? 'bg-white' : (isHomePage ? 'bg-transparent' : 'bg-white')}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="w-16 sm:w-24 relative group">
            <img
              src={logo}
              alt="JP Auto Detailing - Chicago's Premium Detailing Service"
              className="logo transition-all duration-300 group-hover:scale-105"
            />
            <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#3A98B9] transition-all duration-500 group-hover:w-full"></div>
          </Link>
          
          {/* Center Navigation - desktop only */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            <div className="relative group">
              <Link 
                to="/" 
                className={`${textColorClass} hover:text-[#3A98B9] py-2 font-medium transition-all duration-300 tracking-wide text-sm lg:text-base`}
              >
                Premium Detailing
              </Link>
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-transparent group-hover:bg-[#3A98B9] transition-all duration-300 transform origin-left scale-x-0 group-hover:scale-x-100"></div>
            </div>
            <div className="relative group">
              <Link 
                to="/ceramic-coating" 
                className={`${textColorClass} hover:text-[#3A98B9] py-2 font-medium transition-all duration-300 tracking-wide text-sm lg:text-base`}
              >
                Ceramic Protection
              </Link>
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-transparent group-hover:bg-[#3A98B9] transition-all duration-300 transform origin-left scale-x-0 group-hover:scale-x-100"></div>
            </div>
            <div className="relative group">
              <Link 
                to="/dealership-fleet" 
                className={`${textColorClass} hover:text-[#3A98B9] py-2 font-medium transition-all duration-300 tracking-wide text-sm lg:text-base`}
              >
                Business Solutions
              </Link>
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-transparent group-hover:bg-[#3A98B9] transition-all duration-300 transform origin-left scale-x-0 group-hover:scale-x-100"></div>
            </div>
            <div className="relative group">
              <Link 
                to="/gift-cards" 
                className={`${textColorClass} hover:text-[#3A98B9] py-2 font-medium transition-all duration-300 tracking-wide text-sm lg:text-base`}
              >
                Gift The Shine
              </Link>
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-transparent group-hover:bg-[#3A98B9] transition-all duration-300 transform origin-left scale-x-0 group-hover:scale-x-100"></div>
            </div>
          </nav>
          
          {/* Right side buttons/mobile menu */}
          <div className="flex items-center gap-3 sm:gap-6">
            <button
              onClick={() => handleNavigation('/book')}
              className="bg-[#3A98B9] text-white px-3 py-1.5 sm:px-6 sm:py-2.5 rounded-lg hover:bg-[#2A87A8] text-xs sm:text-sm font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 transform"
            >
              <span className="hidden sm:inline">Book Your Detail</span>
              <span className="sm:hidden">Book Now</span>
            </button>
            
            {/* Account/Login Button - changes based on auth state */}
            {user ? (
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center gap-2"
                >
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className={`h-4 w-4 ${textColorClass}`} />
                  </div>
                  <span className={`${textColorClass} hover:text-[#3A98B9] text-xs sm:text-sm font-medium transition-all duration-300 hidden sm:block`}>
                    {userProfile?.firstName || 'My Account'}
                  </span>
                  <ChevronDown className={`h-4 w-4 ${textColorClass} hidden sm:block`} />
                </button>
                
                {/* User dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                    <Link
                      to="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      My Account
                    </Link>
                    <Link
                      to="/account"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/account', { state: { tab: 'appointments' } });
                      }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      My Appointments
                    </Link>
                    <Link
                      to="/account"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/account', { state: { tab: 'vehicles' } });
                      }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      My Vehicles
                    </Link>
                    {isAdmin && (
                      <>
                        <hr className="my-1" />
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-[#3A98B9] hover:bg-blue-50 font-medium"
                        >
                          <div className="flex items-center">
                            <Shield className="h-4 w-4 mr-2" />
                            Admin Panel
                          </div>
                        </Link>
                      </>
                    )}
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to="/login" 
                className={`${textColorClass} hover:text-[#3A98B9] text-xs sm:text-sm font-medium transition-all duration-300 hidden sm:block`}
              >
                Sign In
              </Link>
            )}
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden"
              onClick={toggleMobileMenu}
            >
              {mobileMenuOpen ? (
                <X className={`h-6 w-6 ${textColorClass}`} />
              ) : (
                <Menu className={`h-6 w-6 ${textColorClass}`} />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white absolute left-0 right-0 top-full shadow-lg z-50">
            <div className="flex flex-col py-4 px-6">
              <Link 
                to="/" 
                className="py-3 border-b border-gray-100 text-gray-800 hover:text-[#3A98B9]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Premium Detailing
              </Link>
              <Link 
                to="/ceramic-coating" 
                className="py-3 border-b border-gray-100 text-gray-800 hover:text-[#3A98B9]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Ceramic Protection
              </Link>
              <Link 
                to="/dealership-fleet" 
                className="py-3 border-b border-gray-100 text-gray-800 hover:text-[#3A98B9]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Business Solutions
              </Link>
              
              <Link 
                to="/gift-cards" 
                className="py-3 border-b border-gray-100 text-gray-800 hover:text-[#3A98B9]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Gift The Shine
              </Link>
              
              {/* Auth-aware mobile menu items */}
              {user ? (
                <>
                  <Link 
                    to="/account" 
                    className="py-3 border-b border-gray-100 text-gray-800 hover:text-[#3A98B9]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="py-3 text-left text-red-600 hover:text-red-800"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="py-3 border-b border-gray-100 text-gray-800 hover:text-[#3A98B9]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
              )}
              
              <div className="pt-4">
                <button
                  onClick={() => handleNavigation('/book')}
                  className="w-full bg-[#3A98B9] text-white px-4 py-3 rounded-lg hover:bg-[#2A87A8] font-medium transition-all duration-300"
                >
                  Book Your Detail
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
    </div>
  );
};

export default Navbar;